import { useState } from 'react'
import {
  Drawer, Button, Space, Tag, Table, Typography, Divider, Form,
  Input, Select, DatePicker, InputNumber, Checkbox, Row, Col, Popconfirm,
  Modal, Tooltip,
} from 'antd'
import {
  PlusOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined,
  BellOutlined, ThunderboltOutlined, WarningOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'
import type { Vehicle } from '@/types'
import {
  useVehicleMaintenanceSchedules,
  useCreateMaintenanceSchedule,
  useUpdateMaintenanceSchedule,
  useDeleteMaintenanceSchedule,
  useCompleteMaintenanceSchedule,
  useSendMaintenanceReminder,
  useCreateWorkOrderFromMaintenance,
  type MaintenanceSchedule,
} from '@/hooks/useMaintenanceSchedules'
import { useVehicle } from '@/hooks/useVehicles'

const { Text } = Typography
const { Option } = Select

const statoTag = (s: MaintenanceSchedule) => {
  if (s.stato === 'completata') return <Tag color="green">Completata</Tag>
  if (s.stato === 'annullata') return <Tag color="default">Annullata</Tag>
  if (s.stato_scadenza === 'scaduta') return <Tag color="red" icon={<WarningOutlined />}>Scaduta</Tag>
  if (s.stato_scadenza === 'prossima') return <Tag color="orange">In scadenza</Tag>
  return <Tag color="blue">Attiva</Tag>
}

interface Props {
  vehicle: Vehicle | null
  onClose: () => void
}

const VehicleMaintenanceDrawer = ({ vehicle, onClose }: Props) => {
  const navigate = useNavigate()

  const [scheduleModalOpen, setScheduleModalOpen] = useState(false)
  const [editingSchedule, setEditingSchedule] = useState<MaintenanceSchedule | null>(null)
  const [completeModalOpen, setCompleteModalOpen] = useState(false)
  const [completingId, setCompletingId] = useState<number | null>(null)
  const [reminderModalOpen, setReminderModalOpen] = useState(false)
  const [remindingId, setRemindingId] = useState<number | null>(null)

  const [scheduleForm] = Form.useForm()
  const [completeForm] = Form.useForm()
  const [reminderForm] = Form.useForm()

  // Carica dettaglio veicolo con ownerships
  const { data: vehicleDetail } = useVehicle(vehicle?.id ?? 0)
  const vehicleWithOwnerships = vehicle ? (vehicleDetail ?? vehicle) : null

  const { data: schedules = [], isLoading } = useVehicleMaintenanceSchedules(vehicle?.id ?? 0)

  const createMutation = useCreateMaintenanceSchedule()
  const updateMutation = useUpdateMaintenanceSchedule()
  const deleteMutation = useDeleteMaintenanceSchedule()
  const completeMutation = useCompleteMaintenanceSchedule()
  const reminderMutation = useSendMaintenanceReminder()
  const createWOMutation = useCreateWorkOrderFromMaintenance()

  const openCreate = () => {
    setEditingSchedule(null)
    scheduleForm.resetFields()
    setScheduleModalOpen(true)
  }

  const openEdit = (s: MaintenanceSchedule) => {
    setEditingSchedule(s)
    scheduleForm.setFieldsValue({
      ...s,
      data_scadenza: s.data_scadenza ? dayjs(s.data_scadenza) : null,
    })
    setScheduleModalOpen(true)
  }

  const handleScheduleSave = async () => {
    const values = await scheduleForm.validateFields()
    const payload = {
      ...values,
      vehicle_id: vehicle!.id,
      data_scadenza: values.data_scadenza ? values.data_scadenza.format('YYYY-MM-DD') : undefined,
    }
    if (editingSchedule) {
      await updateMutation.mutateAsync({ id: editingSchedule.id, data: payload })
    } else {
      await createMutation.mutateAsync(payload)
    }
    setScheduleModalOpen(false)
  }

  const handleComplete = async () => {
    if (!completingId) return
    const values = completeForm.getFieldsValue()
    await completeMutation.mutateAsync({
      id: completingId,
      data: {
        data_completamento: values.data_completamento?.format('YYYY-MM-DD'),
        km_completamento: values.km_completamento,
        note_completamento: values.note_completamento,
      },
    })
    setCompleteModalOpen(false)
  }

  const handleReminder = async () => {
    if (!remindingId) return
    const values = await reminderForm.validateFields()
    await reminderMutation.mutateAsync({ id: remindingId, data: values })
    setReminderModalOpen(false)
  }

  const handleCreateWorkOrder = async (id: number) => {
    const result = await createWOMutation.mutateAsync(id)
    onClose()
    navigate(`/work-orders?edit=${result.work_order_id}`)
  }

  const attive = schedules.filter(s => s.stato === 'attiva')
  const storico = schedules.filter(s => s.stato !== 'attiva')

  const columns: ColumnsType<MaintenanceSchedule> = [
    {
      title: 'Descrizione',
      dataIndex: 'descrizione',
      render: (v, s) => (
        <Space direction="vertical" size={0}>
          <Text strong>{v}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {s.tipo === 'ordinaria' ? 'Ordinaria' : 'Straordinaria'}
            {s.ricorrente ? ' · Ricorrente' : ''}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Scadenza',
      key: 'scadenza',
      render: (_, s) => (
        <Space direction="vertical" size={0}>
          {s.data_scadenza && <span>{dayjs(s.data_scadenza).format('DD/MM/YYYY')}</span>}
          {s.km_scadenza && <span style={{ fontSize: 11, color: '#888' }}>{s.km_scadenza.toLocaleString()} km</span>}
          {s.data_completamento && s.stato === 'completata' && (
            <span style={{ fontSize: 11, color: '#52c41a' }}>
              ✓ {dayjs(s.data_completamento).format('DD/MM/YYYY')}
              {s.km_completamento ? ` · ${s.km_completamento.toLocaleString()} km` : ''}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: 'Stato',
      key: 'stato',
      render: (_, s) => statoTag(s),
      width: 110,
    },
    {
      title: '',
      key: 'azioni',
      width: 130,
      render: (_, s) => (
        <Space size={4} wrap>
          {s.stato === 'attiva' && (
            <>
              <Tooltip title="Crea Accettazione">
                <Button size="small" type="primary" icon={<ThunderboltOutlined />}
                  loading={createWOMutation.isPending}
                  onClick={() => handleCreateWorkOrder(s.id)} />
              </Tooltip>
              <Tooltip title="Invia reminder">
                <Button size="small" icon={<BellOutlined />} onClick={() => {
                  setRemindingId(s.id)
                  reminderForm.resetFields()
                  setReminderModalOpen(true)
                }} />
              </Tooltip>
              <Tooltip title="Segna completata">
                <Button size="small" icon={<CheckCircleOutlined />} onClick={() => {
                  setCompletingId(s.id)
                  completeForm.resetFields()
                  setCompleteModalOpen(true)
                }} />
              </Tooltip>
            </>
          )}
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(s)} />
          <Popconfirm title="Eliminare?" onConfirm={() => deleteMutation.mutate(s.id)} okText="Sì" cancelText="No">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <>
      <Drawer
        title={vehicle ? `Manutenzioni — ${vehicle.targa} ${vehicle.marca} ${vehicle.modello}` : 'Manutenzioni'}
        open={!!vehicle}
        onClose={onClose}
        width={760}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nuova Pianificazione
          </Button>
        }
      >
        <Text strong>Pianificazioni attive ({attive.length})</Text>
        <Table
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={attive}
          loading={isLoading}
          pagination={false}
          style={{ marginTop: 8 }}
        />

        {storico.length > 0 && (
          <>
            <Divider />
            <Text strong>Storico completate/annullate ({storico.length})</Text>
            <Table
              rowKey="id"
              size="small"
              columns={columns}
              dataSource={storico}
              pagination={false}
              style={{ marginTop: 8 }}
            />
          </>
        )}

        {vehicleWithOwnerships?.ownerships && vehicleWithOwnerships.ownerships.length > 0 && (
          <>
            <Divider />
            <Text strong>Storico Proprietari ({vehicleWithOwnerships.ownerships!.length})</Text>
            <Table
              rowKey="id"
              size="small"
              pagination={false}
              style={{ marginTop: 8 }}
              dataSource={[...vehicleWithOwnerships.ownerships!].reverse()}
              columns={[
                {
                  title: 'Cliente',
                  dataIndex: 'customer_nome',
                  render: (v: string | null) => v || '—',
                },
                {
                  title: 'Dal',
                  dataIndex: 'data_inizio',
                  width: 110,
                  render: (v: string | null) => v ? dayjs(v).format('DD/MM/YYYY') : '—',
                },
                {
                  title: 'Al',
                  dataIndex: 'data_fine',
                  width: 110,
                  render: (v: string | null) => v
                    ? dayjs(v).format('DD/MM/YYYY')
                    : <Tag color="success">Attuale</Tag>,
                },
              ]}
            />
          </>
        )}
      </Drawer>

      {/* Modal crea/modifica */}
      <Modal
        title={editingSchedule ? 'Modifica pianificazione' : 'Nuova pianificazione'}
        open={scheduleModalOpen}
        onOk={handleScheduleSave}
        onCancel={() => setScheduleModalOpen(false)}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        width={560}
        okText="Salva"
        cancelText="Annulla"
      >
        <Form form={scheduleForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="tipo" label="Tipo" rules={[{ required: true }]}>
            <Select>
              <Option value="ordinaria">Ordinaria</Option>
              <Option value="straordinaria">Straordinaria</Option>
            </Select>
          </Form.Item>
          <Form.Item name="descrizione" label="Descrizione" rules={[{ required: true }]}>
            <Input placeholder="es. Tagliando, Cambio olio, Revisione..." />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="data_scadenza" label="Scadenza data">
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="km_scadenza" label="Scadenza km">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="giorni_preavviso" label="Preavviso (gg)" initialValue={30}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="km_preavviso" label="Preavviso (km)" initialValue={1000}>
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="ricorrente" valuePropName="checked" initialValue={false}>
            <Checkbox>Ricorrente</Checkbox>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(p, c) => p.ricorrente !== c.ricorrente}>
            {({ getFieldValue }) => getFieldValue('ricorrente') && (
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item name="intervallo_giorni" label="Ogni (giorni)">
                    <InputNumber style={{ width: '100%' }} min={1} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="intervallo_km" label="Ogni (km)">
                    <InputNumber style={{ width: '100%' }} min={1} />
                  </Form.Item>
                </Col>
              </Row>
            )}
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal completamento */}
      <Modal
        title="Segna completata"
        open={completeModalOpen}
        onOk={handleComplete}
        onCancel={() => setCompleteModalOpen(false)}
        confirmLoading={completeMutation.isPending}
        okText="Conferma"
        cancelText="Annulla"
      >
        <Form form={completeForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="data_completamento" label="Data">
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} defaultValue={dayjs()} />
          </Form.Item>
          <Form.Item name="km_completamento" label="Km">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="note_completamento" label="Note">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal reminder */}
      <Modal
        title="Invia reminder"
        open={reminderModalOpen}
        onOk={handleReminder}
        onCancel={() => setReminderModalOpen(false)}
        confirmLoading={reminderMutation.isPending}
        okText="Invia"
        cancelText="Annulla"
      >
        <Form form={reminderForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="tipo" label="Canale" rules={[{ required: true }]} initialValue="email">
            <Select>
              <Option value="email">Email</Option>
              <Option value="sms">SMS</Option>
              <Option value="whatsapp">WhatsApp</Option>
            </Select>
          </Form.Item>
          <Form.Item name="messaggio_personalizzato" label="Messaggio personalizzato (opzionale)">
            <Input.TextArea rows={4} placeholder="Lascia vuoto per messaggio standard" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default VehicleMaintenanceDrawer
